import { useCallback, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { createIdempotencyKey } from '../utils/idempotencyKey';
import {
  computePlannerAutoAssign,
  type PlannerAutoAssignAvailability,
  type PlannerAutoAssignRule,
  type PlannerAutoAssignMember,
  type PlannerAutoAssignPlan,
  type PlannerAutoAssignUnavailability,
} from './plannerAutoAssign';

const PLANNER_AUTO_ASSIGN_LOOKBACK_DAYS = 84;
const PLANNER_AUTO_ASSIGN_LOOKAHEAD_DAYS = 365;

type PlannerDateWindow = {
  startIso: string;
  endIso: string;
};

export interface PlannerRole {
  id: string;
  name: string;
  area?: string | null;
}

export interface PlannerAssignedUser {
  id: string;
  full_name?: string | null;
  email?: string | null;
  title?: string | null;
  avatar?: string | null;
}

export interface PlannerAssignment {
  id: string;
  service_plan_id: string;
  role_id?: string | null;
  user_id?: string | null;
  notes?: string | null;
  status: string;
  created_at?: string;
  updated_at?: string;
  service_plan_roles?: PlannerRole | PlannerRole[] | null;
  assigned_user?: PlannerAssignedUser | PlannerAssignedUser[] | null;
}

export interface PlannerSection {
  id: string;
  service_plan_id: string;
  name: string;
  sort_order: number;
  created_at?: string;
}

export interface PlannerSubsection {
  id: string;
  service_plan_id: string;
  section_id: string;
  song_template_id?: string | null;
  name: string;
  description?: string | null;
  length_minutes?: number | null;
  length_display?: string | null;
  sort_order: number;
  created_at?: string;
}

export interface PlannerItem {
  id: string;
  service_plan_id: string;
  area: string;
  subsection?: string | null;
  subsection_description?: string | null;
  title: string;
  description?: string | null;
  length_minutes?: number | null;
  sort_order: number;
  created_at?: string;
}

export interface PlannerMediaItem {
  id: string;
  service_plan_id: string;
  category: string;
  media_type: string;
  title: string;
  file_url: string;
  file_path?: string | null;
  file_size?: number | null;
  sort_order: number;
  created_at?: string;
}

export interface PlannerSubsectionAssignment {
  id: string;
  service_plan_id: string;
  subsection_id: string;
  role_id?: string | null;
  user_id: string;
  notes?: string | null;
  status: string;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  service_plan_roles?: PlannerRole | PlannerRole[] | null;
  assigned_user?: PlannerAssignedUser | PlannerAssignedUser[] | null;
}

export interface PlannerServicePlan {
  id: string;
  title: string;
  service_date: string;
  auto_assign_enabled?: boolean | null;
  location?: string | null;
  start_time?: string | null;
  status?: 'draft' | 'published' | 'archived' | 'cancelled' | string | null;
  notes?: string | null;
  service_timezone?: string | null;
  service_ends_at?: string | null;
  published_at?: string | null;
  archived_at?: string | null;
  cancelled_at?: string | null;
  deleted_at?: string | null;
  lock_version?: number;
  created_by?: string | null;
  created_at?: string;
  service_plan_items?: PlannerItem[];
  service_plan_sections?: PlannerSection[];
  service_plan_subsections?: PlannerSubsection[];
  service_plan_media_items?: PlannerMediaItem[];
  service_plan_assignments?: PlannerAssignment[];
  service_plan_subsection_assignments?: PlannerSubsectionAssignment[];
}

export interface PlannerUserProfile {
  id: string;
  full_name?: string | null;
  email?: string | null;
  title?: string | null;
  avatar?: string | null;
  phone?: string | null;
  roles?: string[] | null;
  default_service_plan_role_id?: string | null;
}

export interface PlannerMemberRecord {
  id: string;
  full_name?: string | null;
  email?: string | null;
  title?: string | null;
  avatar?: string | null;
  roles?: string[] | null;
  default_service_plan_role_id?: string | null;
  primary_service_plan_role_id?: string | null;
  secondary_service_plan_role_ids?: string[];
}

export interface PlannerAssignmentWithPlan {
  assignment: PlannerAssignment;
  plan: PlannerServicePlan;
}

export interface PlannerUpcomingPlanDay {
  dateKey: string;
  serviceDate: string;
  plans: PlannerServicePlan[];
}

export interface PlannerPersonSummary {
  id: string;
  name: string;
  email: string | null;
  title: string | null;
  avatar: string | null;
  statuses: string[];
  roleNames: string[];
  plannerAccessRoles: string[];
  defaultPlannerRoleId: string | null;
  primaryPlannerRoleId: string | null;
  secondaryPlannerRoleIds: string[];
  assignmentsCount: number;
  nextServiceDate: string | null;
}

export interface PlannerAvailability {
  id: string;
  service_plan_id: string;
  user_id: string;
  availability_status: string;
  notes?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface PlannerMemberUnavailability {
  id: string;
  user_id: string;
  starts_at: string;
  ends_at: string;
  all_day?: boolean;
  title?: string | null;
  reason?: string | null;
  notes?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface PlannerTeamRequiredRole {
  id: string;
  team_id: string;
  role_id: string;
  created_by?: string | null;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface PlannerTeamManagerRecord {
  id: string;
  user_id: string;
  team_id: string;
  created_by?: string | null;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface PlannerAutoAssignRuleRecord extends PlannerAutoAssignRule {
  id: string;
  created_by?: string | null;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
  rule_members?: Array<{
    id: string;
    rule_id: string;
    user_id: string;
    priority: number;
    created_at?: string;
    updated_at?: string;
  }>;
}

export interface PlannerPersonDetails {
  id: string;
  name: string;
  email: string | null;
  title: string | null;
  avatar: string | null;
  plannerAccessRoles: string[];
  defaultPlannerRoleId: string | null;
  primaryPlannerRoleId: string | null;
  secondaryPlannerRoleIds: string[];
  upcomingAssignments: PlannerAssignmentWithPlan[];
  historyAssignments: PlannerAssignmentWithPlan[];
  availability: PlannerAvailability[];
}

interface PlannerMemberRolePreference {
  id: string;
  user_id: string;
  role_id: string;
  is_primary: boolean;
  sort_order: number;
}

export interface PlannerServiceTemplate {
  id: string;
  name: string;
  description?: string | null;
  is_active: boolean;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface PlannerServiceTemplateSection {
  id: string;
  template_id: string;
  name: string;
  sort_order: number;
}

interface PlannerServiceTemplateSubsection {
  id: string;
  template_section_id: string;
  song_template_id?: string | null;
  name: string;
  description?: string | null;
  length_minutes?: number | null;
  length_display?: string | null;
  sort_order: number;
}

export interface NewPlannerServicePlan {
  title: string;
  service_date: string;
  start_time?: string | null;
  auto_assign_enabled?: boolean;
  location?: string | null;
  notes?: string | null;
  created_by?: string | null;
}

export interface NewPlannerRole {
  name: string;
  area?: string | null;
  is_active?: boolean;
}

export interface NewPlannerAssignment {
  service_plan_id: string;
  role_id?: string | null;
  user_id?: string | null;
  notes?: string | null;
  status?: string;
}

export interface NewPlannerSubsectionAssignment {
  service_plan_id: string;
  subsection_id: string;
  role_id?: string | null;
  user_id: string;
  notes?: string | null;
  status?: string;
}

export interface SavePlannerTemplateInput {
  service_plan_id: string;
  name: string;
  description?: string | null;
  created_by?: string | null;
}

export interface NewPlannerServiceTemplate {
  name: string;
  description?: string | null;
  created_by?: string | null;
}

export interface ApplyPlannerTemplateInput {
  service_plan_id: string;
  template_id: string;
}

export interface DuplicatePlannerServicePlanStructureInput {
  source_plan_id: string;
  target_plan_id: string;
}

export interface ReorderPlannerSubsectionInput {
  service_plan_id: string;
  section_id: string;
  subsection_id: string;
  direction: 'up' | 'down';
}

export interface ReorderPlannerSubsectionResult {
  moved: boolean;
}

export interface DeletePlannerSubsectionInput {
  service_plan_id: string;
  section_id: string;
  subsection_id: string;
}

export interface DeletePlannerSubsectionResult {
  deleted: boolean;
}

export interface RenamePlannerSectionInput {
  service_plan_id: string;
  section_id: string;
  name: string;
}

export interface RenamePlannerSectionResult {
  renamed: boolean;
}

export interface DeletePlannerSectionInput {
  service_plan_id: string;
  section_id: string;
}

export interface DeletePlannerSectionResult {
  deleted: boolean;
}

export interface UpsertPlannerAvailabilityInput {
  service_plan_id: string;
  user_id: string;
  availability_status: 'available' | 'unavailable' | 'maybe';
  notes?: string | null;
  updated_by?: string | null;
}

export interface RespondToPlannerServiceInput {
  service_plan_id: string;
  user_id: string;
  response_status: 'confirmed' | 'declined' | 'tentative' | 'available' | 'unavailable' | 'maybe';
  notes?: string | null;
  updated_by?: string | null;
}

export interface CreatePlannerMemberUnavailabilityInput {
  user_id: string;
  starts_at: string;
  ends_at: string;
  all_day?: boolean;
  title?: string | null;
  reason?: string | null;
  notes?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
}

export interface UpdatePlannerMemberUnavailabilityInput {
  starts_at?: string;
  ends_at?: string;
  all_day?: boolean;
  title?: string | null;
  reason?: string | null;
  notes?: string | null;
  updated_by?: string | null;
}

export interface NewPlannerTeamRequiredRole {
  team_id: string;
  role_id: string;
  created_by?: string | null;
  updated_by?: string | null;
}

export interface ReplacePlannerMemberSecondaryRolesInput {
  userId: string;
  roleIds: string[];
}

export interface BulkUpdatePlannerMemberRolesInput {
  userIds: string[];
  primaryRoleAction?: 'no_change' | 'clear' | 'set';
  primaryRoleId?: string | null;
  addSecondaryRoleIds?: string[];
  removeSecondaryRoleIds?: string[];
}

export interface BulkUpdatePlannerMemberRolesResult {
  updatedUserIds: string[];
  skippedUserIds: string[];
  failed: Array<{
    userId: string;
    message: string;
  }>;
}

function isMissingPlannerMemberRolesTableError(error: unknown): boolean {
  const code = String((error as { code?: string } | null)?.code || '');
  const message = String((error as { message?: string } | null)?.message || '').toLowerCase();
  return code === '42P01' || message.includes('service_planner_member_roles');
}

function isMissingPlannerRpcError(error: unknown): boolean {
  const code = String((error as { code?: string } | null)?.code || '');
  const message = String((error as { message?: string } | null)?.message || '').toLowerCase();
  return code === '42883' || code === 'PGRST202' || (message.includes('function') && message.includes('not found'));
}

function isMissingPlannerColumnError(error: unknown): boolean {
  const code = String((error as { code?: string } | null)?.code || '');
  const message = String((error as { message?: string } | null)?.message || '').toLowerCase();
  return code === '42703' || message.includes('does not exist');
}

function mapPlannerMemberRolePreferences(
  rows: PlannerMemberRolePreference[]
): Map<string, { primaryRoleId: string | null; secondaryRoleIds: string[] }> {
  const grouped = new Map<string, PlannerMemberRolePreference[]>();
  rows.forEach((row) => {
    if (!row.user_id || !row.role_id) return;
    const entries = grouped.get(row.user_id) ?? [];
    entries.push(row);
    grouped.set(row.user_id, entries);
  });

  const mapped = new Map<string, { primaryRoleId: string | null; secondaryRoleIds: string[] }>();
  grouped.forEach((entries, userId) => {
    const sorted = [...entries].sort((a, b) => {
      const primaryDiff = Number(b.is_primary) - Number(a.is_primary);
      if (primaryDiff !== 0) return primaryDiff;
      const sortDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (sortDiff !== 0) return sortDiff;
      return a.id.localeCompare(b.id);
    });

    const primaryRoleId = sorted.find((row) => row.is_primary)?.role_id || null;
    const secondaryRoleIds = sorted
      .filter((row) => !row.is_primary && row.role_id !== primaryRoleId)
      .map((row) => row.role_id);

    mapped.set(userId, { primaryRoleId, secondaryRoleIds });
  });

  return mapped;
}

const SERVICE_PLAN_SELECT = `
  id,
  title,
  service_date,
  auto_assign_enabled,
  start_time,
  status,
  location,
  notes,
  service_timezone,
  service_ends_at,
  published_at,
  archived_at,
  cancelled_at,
  deleted_at,
  lock_version,
  created_by,
  created_at,
  service_plan_items (
    id,
    service_plan_id,
    area,
    subsection,
    subsection_description,
    title,
    description,
    length_minutes,
    sort_order,
    created_at
  ),
  service_plan_sections (
    id,
    service_plan_id,
    name,
    sort_order,
    created_at
  ),
  service_plan_subsections (
    id,
    service_plan_id,
    section_id,
    song_template_id,
    name,
    description,
    length_minutes,
    length_display,
    sort_order,
    created_at
  ),
  service_plan_media_items (
    id,
    service_plan_id,
    category,
    media_type,
    title,
    file_url,
    file_path,
    file_size,
    sort_order,
    created_at
  ),
  service_plan_assignments (
    id,
    service_plan_id,
    role_id,
    user_id,
    notes,
    status,
    created_at,
    updated_at,
    service_plan_roles (
      id,
      name,
      area
    ),
    assigned_user:users (
      id,
      full_name,
      email,
      title,
      avatar
    )
  ),
  service_plan_subsection_assignments (
    id,
    service_plan_id,
    subsection_id,
    role_id,
    user_id,
    notes,
    status,
    created_by,
    created_at,
    updated_at,
    service_plan_roles (
      id,
      name,
      area
    ),
    assigned_user:users!service_plan_subsection_assignments_user_id_fkey (
      id,
      full_name,
      email,
      title,
      avatar
    )
  )
`;

const LEGACY_SERVICE_PLAN_SELECT = `
  id,
  title,
  service_date,
  auto_assign_enabled,
  start_time,
  location,
  notes,
  created_by,
  created_at,
  service_plan_items (
    id,
    service_plan_id,
    area,
    subsection,
    subsection_description,
    title,
    description,
    length_minutes,
    sort_order,
    created_at
  ),
  service_plan_sections (
    id,
    service_plan_id,
    name,
    sort_order,
    created_at
  ),
  service_plan_subsections (
    id,
    service_plan_id,
    section_id,
    song_template_id,
    name,
    description,
    length_minutes,
    length_display,
    sort_order,
    created_at
  ),
  service_plan_media_items (
    id,
    service_plan_id,
    category,
    media_type,
    title,
    file_url,
    file_path,
    file_size,
    sort_order,
    created_at
  ),
  service_plan_assignments (
    id,
    service_plan_id,
    role_id,
    user_id,
    notes,
    status,
    created_at,
    service_plan_roles (
      id,
      name,
      area
    ),
    assigned_user:users (
      id,
      full_name,
      email,
      title,
      avatar
    )
  ),
  service_plan_subsection_assignments (
    id,
    service_plan_id,
    subsection_id,
    role_id,
    user_id,
    notes,
    status,
    created_by,
    created_at,
    updated_at,
    service_plan_roles (
      id,
      name,
      area
    ),
    assigned_user:users!service_plan_subsection_assignments_user_id_fkey (
      id,
      full_name,
      email,
      title,
      avatar
    )
  )
`;

async function fetchServicePlans(): Promise<PlannerServicePlan[]> {
  const result = await supabase
    .from('service_plans')
    .select(SERVICE_PLAN_SELECT)
    .order('service_date', { ascending: true });

  if (result.error && isMissingPlannerColumnError(result.error)) {
    const legacyResult = await supabase
      .from('service_plans')
      .select(LEGACY_SERVICE_PLAN_SELECT)
      .order('service_date', { ascending: true });

    if (legacyResult.error) throw legacyResult.error;
    return (legacyResult.data as PlannerServicePlan[] | null) ?? [];
  }

  if (result.error) throw result.error;
  return (result.data as PlannerServicePlan[] | null) ?? [];
}

async function fetchServicePlanById(servicePlanId: string): Promise<PlannerServicePlan | null> {
  const result = await supabase
    .from('service_plans')
    .select(SERVICE_PLAN_SELECT)
    .eq('id', servicePlanId)
    .maybeSingle();

  if (result.error && isMissingPlannerColumnError(result.error)) {
    const legacyResult = await supabase
      .from('service_plans')
      .select(LEGACY_SERVICE_PLAN_SELECT)
      .eq('id', servicePlanId)
      .maybeSingle();

    if (legacyResult.error) throw legacyResult.error;
    return (legacyResult.data as PlannerServicePlan | null) ?? null;
  }

  if (result.error) throw result.error;
  return (result.data as PlannerServicePlan | null) ?? null;
}

async function fetchCurrentUserProfile(userId: string): Promise<PlannerUserProfile | null> {
  const { data, error } = await supabase
    .from('users')
    .select('id, full_name, email, title, avatar, phone, roles, default_service_plan_role_id')
    .eq('id', userId)
    .maybeSingle();

  if (error) throw error;
  return (data as PlannerUserProfile) ?? null;
}

async function fetchPlannerRoles(): Promise<PlannerRole[]> {
  const { data, error } = await supabase
    .from('service_plan_roles')
    .select('id, name, area, is_active')
    .eq('is_active', true)
    .order('name', { ascending: true });

  if (error) throw error;
  return (data as PlannerRole[]) ?? [];
}

async function fetchPlannerMembers(): Promise<PlannerMemberRecord[]> {
  const { data, error } = await supabase
    .from('users_directory')
    .select('id, full_name, email, title, avatar, roles, default_service_plan_role_id')
    .overlaps('roles', ['service_planner', 'service_planner_admin', 'service_planner_team_leader'])
    .order('full_name', { ascending: true });

  if (error) throw error;

  const members = (data as PlannerMemberRecord[]) ?? [];
  if (!members.length) return [];

  const userIds = members.map((member) => member.id);
  const { data: prefData, error: prefError } = await supabase
    .from('service_planner_member_roles')
    .select('id, user_id, role_id, is_primary, sort_order')
    .in('user_id', userIds)
    .order('is_primary', { ascending: false })
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (prefError) {
    if (!isMissingPlannerMemberRolesTableError(prefError)) {
      throw prefError;
    }

    return members.map((member) => ({
      ...member,
      primary_service_plan_role_id: member.default_service_plan_role_id ?? null,
      secondary_service_plan_role_ids: [],
    }));
  }

  const prefMap = mapPlannerMemberRolePreferences((prefData as PlannerMemberRolePreference[] | null) ?? []);

  return members.map((member) => {
    const pref = prefMap.get(member.id);
    const primaryRoleId = pref?.primaryRoleId ?? member.default_service_plan_role_id ?? null;
    return {
      ...member,
      default_service_plan_role_id: primaryRoleId,
      primary_service_plan_role_id: primaryRoleId,
      secondary_service_plan_role_ids: pref?.secondaryRoleIds ?? [],
    };
  });
}

async function fetchServicePlanAvailability(): Promise<PlannerAvailability[]> {
  const { data, error } = await supabase
    .from('service_plan_availability')
    .select('id, service_plan_id, user_id, availability_status, notes, created_by, updated_by, created_at, updated_at')
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return (data as PlannerAvailability[]) ?? [];
}

async function fetchMemberUnavailability(): Promise<PlannerMemberUnavailability[]> {
  const { data, error } = await supabase
    .from('service_member_unavailability')
    .select('id, user_id, starts_at, ends_at, all_day, title, reason, notes, created_by, updated_by, created_at, updated_at')
    .order('starts_at', { ascending: true });

  if (error) throw error;
  return (data as PlannerMemberUnavailability[]) ?? [];
}

async function fetchServiceTemplates(): Promise<PlannerServiceTemplate[]> {
  const { data, error } = await supabase
    .from('service_plan_templates')
    .select('*')
    .eq('is_active', true)
    .order('name', { ascending: true });

  if (error) throw error;
  return (data as PlannerServiceTemplate[]) ?? [];
}

function addDays(value: Date, days: number): Date {
  const next = new Date(value);
  next.setDate(next.getDate() + days);
  return next;
}

function buildPlannerDateWindow(anchorIso: string): PlannerDateWindow {
  const anchor = new Date(anchorIso);
  const safeAnchor = Number.isNaN(anchor.getTime()) ? new Date() : anchor;
  return {
    startIso: addDays(safeAnchor, -PLANNER_AUTO_ASSIGN_LOOKBACK_DAYS).toISOString(),
    endIso: addDays(safeAnchor, PLANNER_AUTO_ASSIGN_LOOKAHEAD_DAYS).toISOString(),
  };
}

async function fetchPlannerTeamRequiredRoles(): Promise<PlannerTeamRequiredRole[]> {
  const { data, error } = await supabase
    .from('service_plan_team_required_roles')
    .select('id, team_id, role_id, created_by, updated_by, created_at, updated_at')
    .order('team_id', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data as PlannerTeamRequiredRole[]) ?? [];
}

async function fetchPlannerTeamManagers(): Promise<PlannerTeamManagerRecord[]> {
  const { data, error } = await supabase
    .from('service_planner_team_managers')
    .select('id, user_id, team_id, created_by, updated_by, created_at, updated_at')
    .order('team_id', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data as PlannerTeamManagerRecord[]) ?? [];
}

async function setPlannerTeamManagerTeams({
  userId,
  teamIds,
}: {
  userId: string;
  teamIds: string[];
}): Promise<string[]> {
  const normalizedUserId = userId.trim();
  const normalizedTeamIds = Array.from(
    new Set((teamIds || []).map((teamId) => teamId.trim().toLowerCase()).filter(Boolean))
  );

  const { data, error } = await supabase.rpc('set_service_planner_team_manager_teams', {
    p_target_user_id: normalizedUserId,
    p_team_ids: normalizedTeamIds,
  });

  if (error) throw error;
  return Array.isArray(data) ? data.filter(Boolean) : [];
}

async function fetchPlannerAutoAssignRules(): Promise<PlannerAutoAssignRuleRecord[]> {
  const { data, error } = await supabase
    .from('service_plan_auto_assign_rules')
    .select(`
      id,
      service_plan_id,
      role_id,
      fallback_to_default,
      created_by,
      updated_by,
      created_at,
      updated_at,
      rule_members:service_plan_auto_assign_rule_members (
        id,
        rule_id,
        user_id,
        priority,
        created_at,
        updated_at
      )
    `)
    .order('service_plan_id', { ascending: true })
    .order('created_at', { ascending: true })
    .order('priority', { referencedTable: 'service_plan_auto_assign_rule_members', ascending: true });

  if (error) throw error;

  return ((data as PlannerAutoAssignRuleRecord[] | null) ?? []).map((rule) => ({
    ...rule,
    service_plan_id: rule.service_plan_id ?? null,
    fallback_to_default: rule.fallback_to_default ?? true,
    rule_members: [...(rule.rule_members ?? [])].sort(
      (a, b) => (a.priority ?? 0) - (b.priority ?? 0) || a.user_id.localeCompare(b.user_id)
    ),
  }));
}

async function createServicePlan(payload: NewPlannerServicePlan): Promise<PlannerServicePlan> {
  const result = await supabase
    .from('service_plans')
    .insert(payload)
    .select('id, title, service_date, auto_assign_enabled, start_time, status, location, notes, service_timezone, service_ends_at, lock_version, created_by, created_at')
    .single();

  if (result.error && isMissingPlannerColumnError(result.error)) {
    const legacyResult = await supabase
      .from('service_plans')
      .insert(payload)
      .select('id, title, service_date, auto_assign_enabled, start_time, location, notes, created_by, created_at')
      .single();

    if (legacyResult.error) throw legacyResult.error;
    return legacyResult.data as PlannerServicePlan;
  }

  if (result.error) throw result.error;
  return result.data as PlannerServicePlan;
}

async function deleteServicePlan(servicePlanId: string): Promise<void> {
  const { error } = await supabase
    .from('service_plans')
    .delete()
    .eq('id', servicePlanId);

  if (error) throw error;
}

async function createPlannerRole(payload: NewPlannerRole): Promise<PlannerRole> {
  const normalized = {
    name: payload.name.trim(),
    area: payload.area?.trim() || null,
    is_active: payload.is_active ?? true,
  };

  const { data, error } = await supabase
    .from('service_plan_roles')
    .insert(normalized)
    .select('id, name, area')
    .single();

  if (error) throw error;
  return data as PlannerRole;
}

async function deletePlannerRole(roleId: string): Promise<void> {
  const normalizedRoleId = roleId.trim();
  if (!normalizedRoleId) {
    throw new Error('Role id is required.');
  }

  const { error } = await supabase
    .from('service_plan_roles')
    .delete()
    .eq('id', normalizedRoleId);

  if (error) throw error;
}

async function assertPlannerMemberAvailableForService(servicePlanId: string, userId: string): Promise<void> {
  const planResult = await supabase
    .from('service_plans')
    .select('service_date, service_ends_at')
    .eq('id', servicePlanId)
    .single();

  let planRow = (planResult.data as { service_date?: string | null; service_ends_at?: string | null } | null) ?? null;
  let planError = planResult.error;

  if (planError && isMissingPlannerColumnError(planError)) {
    const legacyPlanResult = await supabase
      .from('service_plans')
      .select('service_date')
      .eq('id', servicePlanId)
      .single();

    planRow =
      (legacyPlanResult.data as { service_date?: string | null; service_ends_at?: string | null } | null) ?? null;
    planError = legacyPlanResult.error;
  }

  if (planError || !planRow?.service_date) {
    throw new Error('Unable to validate assignment availability for this service.');
  }

  const [availabilityResult, overlapResult] = await Promise.all([
    supabase
      .from('service_plan_availability')
      .select('availability_status')
      .eq('service_plan_id', servicePlanId)
      .eq('user_id', userId)
      .maybeSingle(),
    supabase
      .from('service_member_unavailability')
      .select('id')
      .eq('user_id', userId)
      .lt('starts_at', planRow.service_ends_at || planRow.service_date)
      .gt('ends_at', planRow.service_date)
      .limit(1),
  ]);

  if (availabilityResult.error || overlapResult.error) {
    throw new Error('Unable to validate assignment availability for this service.');
  }

  const availabilityStatus = String(availabilityResult.data?.availability_status || '').trim().toLowerCase();
  if (availabilityStatus === 'unavailable') {
    throw new Error('Selected member is marked unavailable for this service.');
  }

  if ((overlapResult.data || []).length > 0) {
    throw new Error('Selected member has a calendar unavailability block for this service date.');
  }
}

async function createPlannerAssignment(payload: NewPlannerAssignment): Promise<PlannerAssignment> {
  const { data, error } = await supabase.rpc('create_service_plan_assignment_txn', {
    p_service_plan_id: payload.service_plan_id,
    p_role_id: payload.role_id ?? null,
    p_user_id: payload.user_id ?? null,
    p_notes: payload.notes ?? null,
    p_status: payload.status ?? 'pending',
  });

  if (error) throw error;
  const assignment = Array.isArray(data) ? data[0] : data;
  return assignment as PlannerAssignment;
}

async function updatePlannerAssignment(
  assignmentId: string,
  updates: Partial<Pick<PlannerAssignment, 'user_id' | 'status' | 'notes'>>
): Promise<PlannerAssignment> {
  const { data, error } = await supabase.rpc('update_service_plan_assignment_txn', {
    p_assignment_id: assignmentId,
    p_set_user_id: updates.user_id !== undefined,
    p_user_id: updates.user_id ?? null,
    p_set_status: updates.status !== undefined,
    p_status: updates.status ?? null,
    p_set_notes: updates.notes !== undefined,
    p_notes: updates.notes ?? null,
  });

  if (error) throw error;
  const assignment = Array.isArray(data) ? data[0] : data;
  return assignment as PlannerAssignment;
}

async function deletePlannerAssignment(assignmentId: string): Promise<void> {
  const { error } = await supabase
    .from('service_plan_assignments')
    .delete()
    .eq('id', assignmentId);

  if (error) throw error;
}

async function createPlannerSubsectionAssignment(
  payload: NewPlannerSubsectionAssignment
): Promise<PlannerSubsectionAssignment> {
  await assertPlannerMemberAvailableForService(payload.service_plan_id, payload.user_id);

  const { data, error } = await supabase
    .from('service_plan_subsection_assignments')
    .insert({
      service_plan_id: payload.service_plan_id,
      subsection_id: payload.subsection_id,
      role_id: payload.role_id ?? null,
      user_id: payload.user_id,
      notes: payload.notes ?? null,
      status: payload.status ?? 'pending',
    })
    .select('id, service_plan_id, subsection_id, role_id, user_id, notes, status, created_by, created_at, updated_at')
    .single();

  if (error) throw error;
  return data as PlannerSubsectionAssignment;
}

async function updatePlannerSubsectionAssignment(
  assignmentId: string,
  updates: Partial<Pick<PlannerSubsectionAssignment, 'role_id' | 'user_id' | 'status' | 'notes'>>
): Promise<PlannerSubsectionAssignment> {
  if (updates.user_id !== undefined && updates.user_id !== null) {
    const { data: existingAssignment, error: existingError } = await supabase
      .from('service_plan_subsection_assignments')
      .select('service_plan_id')
      .eq('id', assignmentId)
      .single();

    if (existingError || !existingAssignment?.service_plan_id) {
      throw new Error('Unable to validate assignment availability for this service.');
    }

    await assertPlannerMemberAvailableForService(existingAssignment.service_plan_id, updates.user_id);
  }

  const { data, error } = await supabase
    .from('service_plan_subsection_assignments')
    .update(updates)
    .eq('id', assignmentId)
    .select('id, service_plan_id, subsection_id, role_id, user_id, notes, status, created_by, created_at, updated_at')
    .single();

  if (error) throw error;
  return data as PlannerSubsectionAssignment;
}

async function deletePlannerSubsectionAssignment(assignmentId: string): Promise<void> {
  const { error } = await supabase
    .from('service_plan_subsection_assignments')
    .delete()
    .eq('id', assignmentId);

  if (error) throw error;
}

async function updatePlannerMemberDefaultRole(userId: string, roleId: string | null): Promise<void> {
  const normalizedRoleId = roleId || null;

  const { error: clearPrimaryError } = await supabase
    .from('service_planner_member_roles')
    .update({ is_primary: false })
    .eq('user_id', userId)
    .eq('is_primary', true);

  if (clearPrimaryError && !isMissingPlannerMemberRolesTableError(clearPrimaryError)) {
    throw clearPrimaryError;
  }

  if (normalizedRoleId && !clearPrimaryError) {
    const { error: upsertPrimaryError } = await supabase
      .from('service_planner_member_roles')
      .upsert(
        {
          user_id: userId,
          role_id: normalizedRoleId,
          is_primary: true,
          sort_order: 0,
        },
        { onConflict: 'user_id,role_id' }
      );

    if (upsertPrimaryError && !isMissingPlannerMemberRolesTableError(upsertPrimaryError)) {
      throw upsertPrimaryError;
    }
  }

  const { error } = await supabase
    .from('users')
    .update({ default_service_plan_role_id: normalizedRoleId })
    .eq('id', userId);

  if (error) throw error;
}

async function replacePlannerMemberSecondaryRoles(
  payload: ReplacePlannerMemberSecondaryRolesInput
): Promise<void> {
  const uniqueRoleIds = Array.from(new Set((payload.roleIds || []).filter(Boolean)));

  const { data: existingData, error: existingError } = await supabase
    .from('service_planner_member_roles')
    .select('id, user_id, role_id, is_primary, sort_order')
    .eq('user_id', payload.userId)
    .order('is_primary', { ascending: false })
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (existingError) {
    if (isMissingPlannerMemberRolesTableError(existingError)) {
      return;
    }
    throw existingError;
  }

  const existingRows = (existingData as PlannerMemberRolePreference[] | null) ?? [];
  const primaryRoleId = existingRows.find((row) => row.is_primary)?.role_id || null;
  const normalizedRoleIds = uniqueRoleIds.filter((roleId) => roleId !== primaryRoleId);

  const existingSecondaryRows = existingRows.filter((row) => !row.is_primary);
  const existingSecondaryByRoleId = new Map(existingSecondaryRows.map((row) => [row.role_id, row]));
  const nextRoleIdSet = new Set(normalizedRoleIds);

  const idsToDelete = existingSecondaryRows
    .filter((row) => !nextRoleIdSet.has(row.role_id))
    .map((row) => row.id);

  if (idsToDelete.length) {
    const { error: deleteError } = await supabase
      .from('service_planner_member_roles')
      .delete()
      .in('id', idsToDelete);

    if (deleteError) throw deleteError;
  }

  const rowsToInsert = normalizedRoleIds
    .filter((roleId) => !existingSecondaryByRoleId.has(roleId))
    .map((roleId, index) => ({
      user_id: payload.userId,
      role_id: roleId,
      is_primary: false,
      sort_order: index,
    }));

  if (rowsToInsert.length) {
    const { error: insertError } = await supabase
      .from('service_planner_member_roles')
      .insert(rowsToInsert);

    if (insertError) throw insertError;
  }

  for (let index = 0; index < normalizedRoleIds.length; index += 1) {
    const roleId = normalizedRoleIds[index];
    const { error: updateError } = await supabase
      .from('service_planner_member_roles')
      .update({ sort_order: index })
      .eq('user_id', payload.userId)
      .eq('role_id', roleId)
      .eq('is_primary', false);

    if (updateError) throw updateError;
  }
}

async function upsertPlannerAvailability(payload: UpsertPlannerAvailabilityInput): Promise<PlannerAvailability> {
  const { data, error } = await supabase.rpc('upsert_service_plan_availability_txn', {
    p_service_plan_id: payload.service_plan_id,
    p_user_id: payload.user_id,
    p_availability_status: payload.availability_status,
    p_notes: payload.notes ?? null,
    p_updated_by: payload.updated_by ?? null,
  });

  if (error) throw error;
  const availability = Array.isArray(data) ? data[0] : data;
  return availability as PlannerAvailability;
}

async function respondToPlannerService(payload: RespondToPlannerServiceInput): Promise<unknown> {
  const { data, error } = await supabase.rpc('respond_to_service_plan_txn', {
    p_service_plan_id: payload.service_plan_id,
    p_user_id: payload.user_id,
    p_response_status: payload.response_status,
    p_notes: payload.notes ?? null,
    p_updated_by: payload.updated_by ?? null,
    p_idempotency_key: createIdempotencyKey('web-planner-respond', payload),
  });

  if (error) throw error;
  return data;
}

async function deletePlannerAvailability(servicePlanId: string, userId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_service_plan_availability_txn', {
    p_service_plan_id: servicePlanId,
    p_user_id: userId,
  });

  if (error) throw error;
}

async function createPlannerMemberUnavailability(
  payload: CreatePlannerMemberUnavailabilityInput
): Promise<PlannerMemberUnavailability> {
  const { data, error } = await supabase.rpc('upsert_service_member_unavailability_txn', {
    p_unavailability_id: null,
    p_user_id: payload.user_id,
    p_starts_at: payload.starts_at,
    p_ends_at: payload.ends_at,
    p_all_day: payload.all_day ?? true,
    p_title: payload.title?.trim() || 'Unavailable',
    p_reason: payload.reason?.trim() || null,
    p_notes: payload.notes?.trim() || null,
    p_source_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    p_updated_by: payload.updated_by ?? payload.created_by ?? null,
  });

  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row as PlannerMemberUnavailability;
}

async function updatePlannerMemberUnavailability(
  unavailabilityId: string,
  updates: UpdatePlannerMemberUnavailabilityInput
): Promise<PlannerMemberUnavailability> {
  const { data: existing, error: existingError } = await supabase
    .from('service_member_unavailability')
    .select('id, user_id, starts_at, ends_at, all_day, title, reason, notes')
    .eq('id', unavailabilityId)
    .single();

  if (existingError) throw existingError;

  const { data, error } = await supabase.rpc('upsert_service_member_unavailability_txn', {
    p_unavailability_id: unavailabilityId,
    p_user_id: existing.user_id,
    p_starts_at: updates.starts_at ?? existing.starts_at,
    p_ends_at: updates.ends_at ?? existing.ends_at,
    p_all_day: updates.all_day ?? existing.all_day ?? true,
    p_title: updates.title?.trim() || existing.title || 'Unavailable',
    p_reason: updates.reason !== undefined ? updates.reason?.trim() || null : existing.reason ?? null,
    p_notes: updates.notes !== undefined ? updates.notes?.trim() || null : existing.notes ?? null,
    p_source_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    p_updated_by: updates.updated_by ?? null,
  });

  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row as PlannerMemberUnavailability;
}

async function deletePlannerMemberUnavailability(unavailabilityId: string): Promise<void> {
  const { error } = await supabase
    .from('service_member_unavailability')
    .delete()
    .eq('id', unavailabilityId);

  if (error) throw error;
}

async function addPlannerTeamRequiredRole(payload: NewPlannerTeamRequiredRole): Promise<PlannerTeamRequiredRole> {
  const normalizedTeamId = payload.team_id.trim().toLowerCase();
  const { data, error } = await supabase
    .from('service_plan_team_required_roles')
    .insert({
      team_id: normalizedTeamId,
      role_id: payload.role_id,
      created_by: payload.created_by ?? null,
      updated_by: payload.updated_by ?? payload.created_by ?? null,
    })
    .select('id, team_id, role_id, created_by, updated_by, created_at, updated_at')
    .single();

  if (error) throw error;
  return data as PlannerTeamRequiredRole;
}

async function removePlannerTeamRequiredRole(requiredRoleId: string): Promise<void> {
  const { error } = await supabase
    .from('service_plan_team_required_roles')
    .delete()
    .eq('id', requiredRoleId);

  if (error) throw error;
}

async function saveServiceTemplate(payload: SavePlannerTemplateInput): Promise<PlannerServiceTemplate> {
  const templateName = payload.name.trim();
  if (!templateName) {
    throw new Error('Template name is required.');
  }

  const [sectionsResult, subsectionsResult] = await Promise.all([
    supabase
      .from('service_plan_sections')
      .select('id, service_plan_id, name, sort_order, created_at')
      .eq('service_plan_id', payload.service_plan_id)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true }),
    supabase
      .from('service_plan_subsections')
      .select('id, service_plan_id, section_id, name, description, length_minutes, length_display, sort_order, created_at')
      .eq('service_plan_id', payload.service_plan_id)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true }),
  ]);

  if (sectionsResult.error) throw sectionsResult.error;
  if (subsectionsResult.error) throw subsectionsResult.error;

  const { data: templateData, error: templateError } = await supabase
    .from('service_plan_templates')
    .insert({
      name: templateName,
      description: payload.description?.trim() || null,
      created_by: payload.created_by ?? null,
      is_active: true,
    })
    .select('*')
    .single();

  if (templateError) throw templateError;

  const sections = (sectionsResult.data as PlannerSection[] | null) ?? [];
  const subsections = (subsectionsResult.data as PlannerSubsection[] | null) ?? [];
  const sectionMap = new Map<string, string>();

  for (const section of sections) {
    const { data: templateSection, error: sectionError } = await supabase
      .from('service_plan_template_sections')
      .insert({
        template_id: templateData.id,
        name: section.name,
        sort_order: section.sort_order,
      })
      .select('id')
      .single();

    if (sectionError) throw sectionError;
    sectionMap.set(section.id, (templateSection as { id: string }).id);
  }

  for (const subsection of subsections) {
    const templateSectionId = sectionMap.get(subsection.section_id);
    if (!templateSectionId) continue;

    const { error: subsectionError } = await supabase
      .from('service_plan_template_subsections')
      .insert({
        template_section_id: templateSectionId,
        song_template_id: subsection.song_template_id ?? null,
        name: subsection.name,
        description: subsection.description ?? null,
        length_minutes: subsection.length_minutes ?? null,
        length_display: subsection.length_display ?? null,
        sort_order: subsection.sort_order,
      });

    if (subsectionError) throw subsectionError;
  }

  return templateData as PlannerServiceTemplate;
}

async function createServiceTemplate(payload: NewPlannerServiceTemplate): Promise<PlannerServiceTemplate> {
  const templateName = payload.name.trim();
  if (!templateName) {
    throw new Error('Template name is required.');
  }

  const { data, error } = await supabase
    .from('service_plan_templates')
    .insert({
      name: templateName,
      description: payload.description?.trim() || null,
      created_by: payload.created_by ?? null,
      is_active: true,
    })
    .select('*')
    .single();

  if (error) throw error;
  return data as PlannerServiceTemplate;
}

async function deleteServiceTemplate(templateId: string): Promise<void> {
  const normalizedTemplateId = templateId.trim();
  if (!normalizedTemplateId) {
    throw new Error('Template id is required.');
  }

  const { error } = await supabase
    .from('service_plan_templates')
    .delete()
    .eq('id', normalizedTemplateId);

  if (error) throw error;
}

async function applyServiceTemplate(payload: ApplyPlannerTemplateInput): Promise<void> {
  const { error: rpcError } = await supabase.rpc('apply_service_plan_template_txn', {
    p_template_id: payload.template_id,
    p_service_plan_id: payload.service_plan_id,
    p_idempotency_key: createIdempotencyKey('web-planner-apply-template', payload),
  });

  if (!rpcError) return;

  if (!isMissingPlannerRpcError(rpcError)) {
    throw rpcError;
  }

  const { data: templateSectionsData, error: templateSectionsError } = await supabase
    .from('service_plan_template_sections')
    .select('*')
    .eq('template_id', payload.template_id)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (templateSectionsError) throw templateSectionsError;

  const templateSections = (templateSectionsData as PlannerServiceTemplateSection[] | null) ?? [];
  if (!templateSections.length) return;

  const sectionIds = templateSections.map((section) => section.id);
  const { data: templateSubsectionsData, error: templateSubsectionsError } = await supabase
    .from('service_plan_template_subsections')
    .select('*')
    .in('template_section_id', sectionIds)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (templateSubsectionsError) throw templateSubsectionsError;

  const templateSubsections = (templateSubsectionsData as PlannerServiceTemplateSubsection[] | null) ?? [];
  const { data: existingSectionsData, error: existingSectionsError } = await supabase
    .from('service_plan_sections')
    .select('id, name, sort_order')
    .eq('service_plan_id', payload.service_plan_id)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (existingSectionsError) throw existingSectionsError;

  const existingSections = (existingSectionsData as PlannerSection[] | null) ?? [];
  const existingSectionsByName = new Map<string, PlannerSection>();
  existingSections.forEach((section) => {
    const key = section.name.trim().toLowerCase();
    if (!key || existingSectionsByName.has(key)) return;
    existingSectionsByName.set(key, section);
  });

  let nextSectionSortOrder = existingSections.length
    ? Math.max(...existingSections.map((section) => section.sort_order || 0)) + 1
    : 0;

  const { data: existingSubsectionsData, error: existingSubsectionsError } = await supabase
    .from('service_plan_subsections')
    .select('section_id, sort_order, name')
    .eq('service_plan_id', payload.service_plan_id)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (existingSubsectionsError) throw existingSubsectionsError;

  const nextSubsectionSortBySectionId = new Map<string, number>();
  const subsectionNamesBySectionId = new Map<string, Set<string>>();
  ((existingSubsectionsData as Pick<PlannerSubsection, 'section_id' | 'sort_order' | 'name'>[] | null) ?? []).forEach((subsection) => {
    const current = nextSubsectionSortBySectionId.get(subsection.section_id) ?? 0;
    const candidate = (subsection.sort_order || 0) + 1;
    if (candidate > current) {
      nextSubsectionSortBySectionId.set(subsection.section_id, candidate);
    }

    const subsectionName = subsection.name?.trim().toLowerCase();
    if (!subsectionName) return;
    const sectionNameSet = subsectionNamesBySectionId.get(subsection.section_id) || new Set<string>();
    sectionNameSet.add(subsectionName);
    subsectionNamesBySectionId.set(subsection.section_id, sectionNameSet);
  });

  const sectionMap = new Map<string, string>();

  for (const section of templateSections) {
    const existingSection = existingSectionsByName.get(section.name.trim().toLowerCase());
    if (existingSection) {
      sectionMap.set(section.id, existingSection.id);
      continue;
    }

    const { data: insertedSection, error: insertedSectionError } = await supabase
      .from('service_plan_sections')
      .insert({
        service_plan_id: payload.service_plan_id,
        name: section.name,
        sort_order: nextSectionSortOrder,
      })
      .select('id')
      .single();

    if (insertedSectionError) throw insertedSectionError;
    const insertedSectionId = (insertedSection as { id: string }).id;
    sectionMap.set(section.id, insertedSectionId);
    existingSectionsByName.set(section.name.trim().toLowerCase(), {
      ...section,
      id: insertedSectionId,
      service_plan_id: payload.service_plan_id,
      sort_order: nextSectionSortOrder,
    });
    nextSubsectionSortBySectionId.set(insertedSectionId, 0);
    nextSectionSortOrder += 1;
  }

  for (const subsection of templateSubsections) {
    const mappedSectionId = sectionMap.get(subsection.template_section_id);
    if (!mappedSectionId) continue;
    const nextSortOrder = nextSubsectionSortBySectionId.get(mappedSectionId) ?? 0;
    const normalizedBaseName = subsection.name.trim() || 'Timeslot';
    const sectionNameSet = subsectionNamesBySectionId.get(mappedSectionId) || new Set<string>();
    let resolvedName = normalizedBaseName;
    let suffix = 2;
    while (sectionNameSet.has(resolvedName.toLowerCase())) {
      resolvedName = `${normalizedBaseName} (${suffix})`;
      suffix += 1;
    }

    const { error: insertSubsectionError } = await supabase
      .from('service_plan_subsections')
      .insert({
        service_plan_id: payload.service_plan_id,
        section_id: mappedSectionId,
        song_template_id: subsection.song_template_id ?? null,
        name: resolvedName,
        description: subsection.description ?? null,
        length_minutes: subsection.length_minutes ?? null,
        length_display: subsection.length_display ?? null,
        sort_order: nextSortOrder,
      });

    if (insertSubsectionError) throw insertSubsectionError;
    sectionNameSet.add(resolvedName.toLowerCase());
    subsectionNamesBySectionId.set(mappedSectionId, sectionNameSet);
    nextSubsectionSortBySectionId.set(mappedSectionId, nextSortOrder + 1);
  }
}

async function duplicatePlannerServicePlanStructure(payload: DuplicatePlannerServicePlanStructureInput): Promise<void> {
  const { error: rpcError } = await supabase.rpc('duplicate_service_plan_structure_txn', {
    p_source_plan_id: payload.source_plan_id,
    p_target_plan_id: payload.target_plan_id,
    p_idempotency_key: createIdempotencyKey('web-planner-duplicate-structure', payload),
  });

  if (!rpcError) return;

  if (!isMissingPlannerRpcError(rpcError)) {
    throw rpcError;
  }

  const sourcePlan = await fetchServicePlanById(payload.source_plan_id);
  if (!sourcePlan) {
    throw new Error('Source service plan was not found.');
  }

  const sourceSections = [...(sourcePlan.service_plan_sections ?? [])].sort(
    (left, right) =>
      (left.sort_order ?? 0) - (right.sort_order ?? 0)
      || (left.created_at ?? '').localeCompare(right.created_at ?? '')
  );
  const sectionOrder = new Map(sourceSections.map((section) => [section.id, section.sort_order ?? 0]));
  const sectionIdMap = new Map<string, string>();

  for (const section of sourceSections) {
    const { data, error } = await supabase
      .from('service_plan_sections')
      .insert({
        service_plan_id: payload.target_plan_id,
        name: section.name,
        sort_order: section.sort_order,
      })
      .select('id')
      .single();

    if (error || !data?.id) {
      throw error ?? new Error('Unable to duplicate service section.');
    }

    sectionIdMap.set(section.id, data.id as string);
  }

  const sourceSubsections = [...(sourcePlan.service_plan_subsections ?? [])].sort(
    (left, right) =>
      (sectionOrder.get(left.section_id) ?? 0) - (sectionOrder.get(right.section_id) ?? 0)
      || (left.sort_order ?? 0) - (right.sort_order ?? 0)
      || (left.created_at ?? '').localeCompare(right.created_at ?? '')
  );

  if (sourceSubsections.length) {
    const subsectionPayload = sourceSubsections
      .map((subsection) => {
        const mappedSectionId = sectionIdMap.get(subsection.section_id);
        if (!mappedSectionId) return null;

        return {
          service_plan_id: payload.target_plan_id,
          section_id: mappedSectionId,
          song_template_id: subsection.song_template_id ?? null,
          name: subsection.name,
          description: subsection.description ?? null,
          length_minutes: subsection.length_minutes ?? null,
          length_display: subsection.length_display ?? null,
          sort_order: subsection.sort_order,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry != null);

    if (subsectionPayload.length) {
      const { error } = await supabase
        .from('service_plan_subsections')
        .insert(subsectionPayload);

      if (error) throw error;
    }
  }

  const sourceItems = [...(sourcePlan.service_plan_items ?? [])].sort(
    (left, right) =>
      (left.sort_order ?? 0) - (right.sort_order ?? 0)
      || (left.created_at ?? '').localeCompare(right.created_at ?? '')
  );

  if (sourceItems.length) {
    const itemPayload = sourceItems.map((item) => ({
      service_plan_id: payload.target_plan_id,
      area: item.area,
      subsection: item.subsection ?? null,
      subsection_description: item.subsection_description ?? null,
      title: item.title,
      description: item.description ?? null,
      length_minutes: item.length_minutes ?? null,
      sort_order: item.sort_order,
    }));

    const { error } = await supabase
      .from('service_plan_items')
      .insert(itemPayload);

    if (error) throw error;
  }
}

export async function reorderPlannerSubsection(
  payload: ReorderPlannerSubsectionInput
): Promise<ReorderPlannerSubsectionResult> {
  const direction = payload.direction === 'up' ? 'up' : 'down';
  const { data, error } = await supabase.rpc('reorder_service_plan_subsection', {
    p_service_plan_id: payload.service_plan_id,
    p_section_id: payload.section_id,
    p_subsection_id: payload.subsection_id,
    p_direction: direction,
  });

  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  return {
    moved: Boolean((row as { moved?: boolean } | null | undefined)?.moved),
  };
}

export async function deletePlannerSubsection(
  payload: DeletePlannerSubsectionInput
): Promise<DeletePlannerSubsectionResult> {
  const { data, error } = await supabase.rpc('delete_service_plan_subsection', {
    p_service_plan_id: payload.service_plan_id,
    p_section_id: payload.section_id,
    p_subsection_id: payload.subsection_id,
  });

  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  return {
    deleted: Boolean((row as { deleted?: boolean } | null | undefined)?.deleted),
  };
}

export async function renamePlannerSection(
  payload: RenamePlannerSectionInput
): Promise<RenamePlannerSectionResult> {
  const { data, error } = await supabase.rpc('rename_service_plan_section', {
    p_service_plan_id: payload.service_plan_id,
    p_section_id: payload.section_id,
    p_new_name: payload.name,
  });

  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  return {
    renamed: Boolean((row as { renamed?: boolean } | null | undefined)?.renamed),
  };
}

export async function deletePlannerSection(
  payload: DeletePlannerSectionInput
): Promise<DeletePlannerSectionResult> {
  const { data, error } = await supabase.rpc('delete_service_plan_section', {
    p_service_plan_id: payload.service_plan_id,
    p_section_id: payload.section_id,
  });

  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  return {
    deleted: Boolean((row as { deleted?: boolean } | null | undefined)?.deleted),
  };
}

function normalizeStatus(status: string | null | undefined): string {
  const normalized = status?.trim().toLowerCase() || '';
  if (!normalized) return 'available';
  if (normalized === 'scheduled' || normalized === 'pending') return 'available';
  return normalized;
}

function firstRelation<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

function getLocalDateKey(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function sortPlansByDate(a: PlannerServicePlan, b: PlannerServicePlan): number {
  const timeDiff = new Date(a.service_date).getTime() - new Date(b.service_date).getTime();
  if (timeDiff !== 0) return timeDiff;
  return a.id.localeCompare(b.id);
}

function normalizeRoleKeys(input: string[] | null | undefined): string[] {
  return (input ?? [])
    .map((role) => (typeof role === 'string' ? role.trim().toLowerCase() : ''))
    .filter(Boolean);
}

function plannerDirectoryRoles(input: string[] | null | undefined): string[] {
  return normalizeRoleKeys(input).filter(
    (role) => role === 'service_planner' || role === 'service_planner_admin'
  );
}

function normalizePlannerRoleIdList(input: string[] | null | undefined): string[] {
  return Array.from(new Set((input ?? []).map((roleId) => roleId.trim()).filter(Boolean)));
}

function arePlannerRoleIdListsEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}

type PlannerAutoAssignErrorPayload = {
  success?: false;
  code?: string;
  message?: string;
  error?: string;
  requestId?: string;
  errorId?: string;
  details?: unknown;
};

function getPlannerAutoAssignErrorContext(error: unknown): {
  status?: number;
  headers?: { get?: (name: string) => string | null };
  json?: () => Promise<unknown>;
  clone?: () => unknown;
} | null {
  if (!error || typeof error !== 'object' || !('context' in error)) {
    return null;
  }

  const context = (error as { context?: unknown }).context;
  if (!context || typeof context !== 'object') {
    return null;
  }

  return context as {
    status?: number;
    headers?: { get?: (name: string) => string | null };
    json?: () => Promise<unknown>;
    clone?: () => unknown;
  };
}

async function readPlannerAutoAssignErrorBody(error: unknown): Promise<PlannerAutoAssignErrorPayload | null> {
  const context = getPlannerAutoAssignErrorContext(error);
  if (!context) return null;

  try {
    const response = typeof context.clone === 'function'
      ? (context.clone() as { json?: () => Promise<unknown> })
      : context;

    if (typeof response.json !== 'function') return null;
    const body = await response.json();
    return body && typeof body === 'object' ? (body as PlannerAutoAssignErrorPayload) : null;
  } catch {
    return null;
  }
}

function buildPlannerAutoAssignError(
  message: string,
  body?: PlannerAutoAssignErrorPayload | null,
  error?: unknown,
): Error {
  const context = getPlannerAutoAssignErrorContext(error);
  const plannerError = new Error(message) as Error & {
    code?: string;
    requestId?: string;
    details?: unknown;
    status?: number;
  };

  plannerError.code = body?.code;
  plannerError.requestId = body?.requestId
    || body?.errorId
    || context?.headers?.get?.('x-request-id')
    || context?.headers?.get?.('X-Request-Id')
    || undefined;
  plannerError.details = body?.details;
  plannerError.status = typeof context?.status === 'number' ? context.status : undefined;

  return plannerError;
}

const AUTO_ASSIGN_PLAN_SELECT = `
  id,
  service_date,
  auto_assign_enabled,
  service_plan_assignments (
    id,
    service_plan_id,
    role_id,
    user_id,
    status,
    created_at
  )
`;

async function fetchAutoAssignPlans(window: PlannerDateWindow): Promise<PlannerAutoAssignPlan[]> {
  const { data, error } = await supabase
    .from('service_plans')
    .select(AUTO_ASSIGN_PLAN_SELECT)
    .gte('service_date', window.startIso)
    .lte('service_date', window.endIso)
    .order('service_date', { ascending: true });

  if (error) throw error;
  return (data as PlannerAutoAssignPlan[]) ?? [];
}

async function runPlannerAutoAssignForService(servicePlanId: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('planner-auto-assign', {
    body: { servicePlanId },
  });

  if (error) {
    const body = await readPlannerAutoAssignErrorBody(error);
    throw buildPlannerAutoAssignError(
      body?.message || body?.error || (error instanceof Error ? error.message : 'Planner auto-assign failed.'),
      body,
      error,
    );
  }
  if (!data?.success) {
    const body = data as PlannerAutoAssignErrorPayload;
    throw buildPlannerAutoAssignError(body.message || body.error || 'Planner auto-assign failed.', body);
  }
}

async function fetchServicePlanAutoAssignEnabled(servicePlanId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('service_plans')
    .select('auto_assign_enabled')
    .eq('id', servicePlanId)
    .maybeSingle();

  if (error) throw error;
  return Boolean(data?.auto_assign_enabled);
}

async function runPlannerAutoAssignForUpcomingServices(): Promise<void> {
  const plans = await fetchAutoAssignPlans(buildPlannerDateWindow(new Date().toISOString()));
  const nowMs = Date.now();
  const upcomingPlans = plans
    .filter((plan) => Boolean((plan as PlannerServicePlan).auto_assign_enabled) && new Date(plan.service_date).getTime() >= nowMs)
    .map((plan) => plan.id);

  for (const servicePlanId of upcomingPlans) {
    await runPlannerAutoAssignForService(servicePlanId);
  }
}

export function usePlannerRoles(enabled: boolean) {
  return useQuery({
    queryKey: ['church-planner', 'roles'],
    queryFn: fetchPlannerRoles,
    enabled,
  });
}

export function useChurchPlanner(enabled: boolean, currentUserId?: string) {
  const queryClient = useQueryClient();
  const plansQuery = useQuery({
    queryKey: ['church-planner', 'plans'],
    queryFn: fetchServicePlans,
    enabled,
  });

  const currentUserQuery = useQuery({
    queryKey: ['church-planner', 'current-user', currentUserId ?? ''],
    queryFn: () => fetchCurrentUserProfile(currentUserId as string),
    enabled: enabled && !!currentUserId,
  });

  const templatesQuery = useQuery({
    queryKey: ['church-planner', 'service-templates'],
    queryFn: fetchServiceTemplates,
    enabled,
  });

  const membersQuery = useQuery({
    queryKey: ['church-planner', 'members'],
    queryFn: fetchPlannerMembers,
    enabled,
  });

  const availabilityQuery = useQuery({
    queryKey: ['church-planner', 'availability'],
    queryFn: fetchServicePlanAvailability,
    enabled,
  });

  const memberUnavailabilityQuery = useQuery({
    queryKey: ['church-planner', 'member-unavailability'],
    queryFn: fetchMemberUnavailability,
    enabled,
  });

  const teamRequiredRolesQuery = useQuery({
    queryKey: ['church-planner', 'team-required-roles'],
    queryFn: fetchPlannerTeamRequiredRoles,
    enabled,
  });

  const teamManagersQuery = useQuery({
    queryKey: ['church-planner', 'team-managers'],
    queryFn: fetchPlannerTeamManagers,
    enabled,
  });

  const autoAssignRulesQuery = useQuery({
    queryKey: ['church-planner', 'auto-assign-rules'],
    queryFn: fetchPlannerAutoAssignRules,
    enabled,
  });

  const invalidatePlannerQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['church-planner', 'plans'] }),
      queryClient.invalidateQueries({ queryKey: ['church-planner', 'roles'] }),
      queryClient.invalidateQueries({ queryKey: ['church-planner', 'current-user'] }),
      queryClient.invalidateQueries({ queryKey: ['church-planner', 'service-templates'] }),
      queryClient.invalidateQueries({ queryKey: ['church-planner', 'members'] }),
      queryClient.invalidateQueries({ queryKey: ['church-planner', 'availability'] }),
      queryClient.invalidateQueries({ queryKey: ['church-planner', 'member-unavailability'] }),
      queryClient.invalidateQueries({ queryKey: ['church-planner', 'team-required-roles'] }),
      queryClient.invalidateQueries({ queryKey: ['church-planner', 'team-managers'] }),
      queryClient.invalidateQueries({ queryKey: ['church-planner', 'auto-assign-rules'] }),
    ]);
  }, [queryClient]);

  useEffect(() => {
    if (!enabled) return;

    let invalidateTimer: number | undefined;
    const scheduleInvalidate = () => {
      if (invalidateTimer) {
        window.clearTimeout(invalidateTimer);
      }
      invalidateTimer = window.setTimeout(() => {
        void invalidatePlannerQueries();
      }, 120);
    };

    const channel = supabase.channel(`church-planner-realtime-${currentUserId || 'all'}`);
    const changes = [
      { table: 'service_plans' },
      { table: 'service_plan_sections' },
      { table: 'service_plan_subsections' },
      { table: 'service_plan_items' },
      { table: 'service_plan_roles' },
      { table: 'service_plan_assignments', filter: currentUserId ? `user_id=eq.${currentUserId}` : undefined },
      { table: 'service_plan_subsection_assignments', filter: currentUserId ? `user_id=eq.${currentUserId}` : undefined },
      { table: 'service_plan_media_items' },
      { table: 'service_plan_templates' },
      { table: 'service_plan_template_sections' },
      { table: 'service_plan_template_subsections' },
      { table: 'service_plan_availability', filter: currentUserId ? `user_id=eq.${currentUserId}` : undefined },
      { table: 'service_member_unavailability', filter: currentUserId ? `user_id=eq.${currentUserId}` : undefined },
      { table: 'service_plan_team_required_roles' },
      { table: 'service_planner_team_managers', filter: currentUserId ? `user_id=eq.${currentUserId}` : undefined },
      { table: 'service_plan_auto_assign_rules' },
      { table: 'service_plan_auto_assign_rule_members' },
    ];

    changes.forEach((change) => {
      channel.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: change.table,
          filter: change.filter,
        },
        scheduleInvalidate
      );
    });

    channel.subscribe();

    return () => {
      if (invalidateTimer) {
        window.clearTimeout(invalidateTimer);
      }
      void supabase.removeChannel(channel);
    };
  }, [enabled, currentUserId, invalidatePlannerQueries]);

  const autoAssignServicePlan = useCallback(async (servicePlanId: string) => {
    await runPlannerAutoAssignForService(servicePlanId);
  }, []);

  const maybeAutoAssignServicePlan = useCallback(async (servicePlanId: string, autoAssignEnabled?: boolean | null) => {
    const enabledForPlan = typeof autoAssignEnabled === 'boolean'
      ? autoAssignEnabled
      : await fetchServicePlanAutoAssignEnabled(servicePlanId);

    if (!enabledForPlan) return;
    await runPlannerAutoAssignForService(servicePlanId);
  }, []);

  const autoAssignUpcomingServices = useCallback(async () => {
    await runPlannerAutoAssignForUpcomingServices();
  }, []);

    const getPlanById = useCallback(async (servicePlanId: string) => fetchServicePlanById(servicePlanId), []);

    const duplicatePlanStructure = useCallback(async (payload: DuplicatePlannerServicePlanStructureInput) => {
      await duplicatePlannerServicePlanStructure(payload);
      await invalidatePlannerQueries();
    }, [invalidatePlannerQueries]);

  const createPlanMutation = useMutation({
    mutationFn: createServicePlan,
    onSuccess: async (createdPlan) => {
      if (createdPlan?.id) {
        try {
          await maybeAutoAssignServicePlan(createdPlan.id, createdPlan.auto_assign_enabled);
        } catch (error) {
          // Keep plan creation successful even if auto-assign fails.
          console.error('Planner auto-assign failed after plan creation:', error);
        }
      }
      await invalidatePlannerQueries();
    },
  });

  const deletePlanMutation = useMutation({
    mutationFn: deleteServicePlan,
    onSuccess: invalidatePlannerQueries,
  });

  const createRoleMutation = useMutation({
    mutationFn: createPlannerRole,
    onSuccess: invalidatePlannerQueries,
  });

  const deleteRoleMutation = useMutation({
    mutationFn: deletePlannerRole,
    onSuccess: async () => {
      try {
        await autoAssignUpcomingServices();
      } catch (error) {
        console.error('Planner auto-assign failed after role deletion:', error);
      }
      await invalidatePlannerQueries();
    },
  });

  const createAssignmentMutation = useMutation({
    mutationFn: createPlannerAssignment,
    onSuccess: invalidatePlannerQueries,
  });

  const updateAssignmentMutation = useMutation({
    mutationFn: ({
      assignmentId,
      updates,
    }: {
      assignmentId: string;
      updates: Partial<Pick<PlannerAssignment, 'user_id' | 'status' | 'notes'>>;
    }) => updatePlannerAssignment(assignmentId, updates),
    onSuccess: async (assignment, vars) => {
      if (assignment?.service_plan_id && (vars.updates.user_id !== undefined || vars.updates.status !== undefined)) {
        try {
          await maybeAutoAssignServicePlan(assignment.service_plan_id);
        } catch (error) {
          console.error('Planner auto-assign failed after assignment update:', error);
        }
      }

      await invalidatePlannerQueries();
    },
  });

  const deleteAssignmentMutation = useMutation({
    mutationFn: deletePlannerAssignment,
    onSuccess: invalidatePlannerQueries,
  });

  const createSubsectionAssignmentMutation = useMutation({
    mutationFn: createPlannerSubsectionAssignment,
    onSuccess: invalidatePlannerQueries,
  });

  const updateSubsectionAssignmentMutation = useMutation({
    mutationFn: ({
      assignmentId,
      updates,
    }: {
      assignmentId: string;
      updates: Partial<Pick<PlannerSubsectionAssignment, 'role_id' | 'user_id' | 'status' | 'notes'>>;
    }) => updatePlannerSubsectionAssignment(assignmentId, updates),
    onSuccess: invalidatePlannerQueries,
  });

  const deleteSubsectionAssignmentMutation = useMutation({
    mutationFn: deletePlannerSubsectionAssignment,
    onSuccess: invalidatePlannerQueries,
  });

  const reorderSubsectionMutation = useMutation({
    mutationFn: reorderPlannerSubsection,
    onSuccess: invalidatePlannerQueries,
  });

  const deleteSubsectionMutation = useMutation({
    mutationFn: deletePlannerSubsection,
    onSuccess: invalidatePlannerQueries,
  });

  const renameSectionMutation = useMutation({
    mutationFn: renamePlannerSection,
    onSuccess: invalidatePlannerQueries,
  });

  const deleteSectionMutation = useMutation({
    mutationFn: deletePlannerSection,
    onSuccess: invalidatePlannerQueries,
  });

  const saveTemplateMutation = useMutation({
    mutationFn: saveServiceTemplate,
    onSuccess: invalidatePlannerQueries,
  });

  const createTemplateMutation = useMutation({
    mutationFn: createServiceTemplate,
    onSuccess: invalidatePlannerQueries,
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: deleteServiceTemplate,
    onSuccess: invalidatePlannerQueries,
  });

  const applyTemplateMutation = useMutation({
    mutationFn: applyServiceTemplate,
    onSuccess: invalidatePlannerQueries,
  });

  const updateMemberDefaultRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string | null }) =>
      updatePlannerMemberDefaultRole(userId, roleId),
    onSuccess: async () => {
      try {
        await autoAssignUpcomingServices();
      } catch (error) {
        console.error('Planner auto-assign failed after default role update:', error);
      }
      await invalidatePlannerQueries();
    },
  });

  const updateMemberSecondaryRolesMutation = useMutation({
    mutationFn: replacePlannerMemberSecondaryRoles,
    onSuccess: async () => {
      try {
        await autoAssignUpcomingServices();
      } catch (error) {
        console.error('Planner auto-assign failed after secondary role update:', error);
      }
      await invalidatePlannerQueries();
    },
  });

  const bulkUpdateMemberRolesMutation = useMutation({
    mutationFn: async (
      payload: BulkUpdatePlannerMemberRolesInput
    ): Promise<BulkUpdatePlannerMemberRolesResult> => {
      const normalizedUserIds = Array.from(new Set((payload.userIds ?? []).map((userId) => userId.trim()).filter(Boolean)));
      if (!normalizedUserIds.length) {
        return { updatedUserIds: [], skippedUserIds: [], failed: [] };
      }

      const addSecondaryRoleIds = normalizePlannerRoleIdList(payload.addSecondaryRoleIds);
      const removeSecondaryRoleIdSet = new Set(normalizePlannerRoleIdList(payload.removeSecondaryRoleIds));
      const primaryRoleAction = payload.primaryRoleAction ?? 'no_change';
      const selectedPrimaryRoleId = primaryRoleAction === 'set' ? payload.primaryRoleId?.trim() || null : null;
      const membersById = new Map((membersQuery.data ?? []).map((member) => [member.id, member]));

      const tasks = normalizedUserIds.map(async (userId) => {
        const member = membersById.get(userId);
        const currentPrimaryRoleId = member?.primary_service_plan_role_id ?? member?.default_service_plan_role_id ?? null;
        const currentSecondaryRoleIds = normalizePlannerRoleIdList(member?.secondary_service_plan_role_ids);

        let nextPrimaryRoleId = currentPrimaryRoleId;
        if (primaryRoleAction === 'clear') {
          nextPrimaryRoleId = null;
        } else if (primaryRoleAction === 'set') {
          nextPrimaryRoleId = selectedPrimaryRoleId;
        }

        const nextSecondaryRoleIds = currentSecondaryRoleIds.filter((roleId) => !removeSecondaryRoleIdSet.has(roleId));
        addSecondaryRoleIds.forEach((roleId) => {
          if (roleId === nextPrimaryRoleId || nextSecondaryRoleIds.includes(roleId)) return;
          nextSecondaryRoleIds.push(roleId);
        });

        const normalizedNextSecondaryRoleIds = nextSecondaryRoleIds.filter((roleId) => roleId !== nextPrimaryRoleId);
        const primaryChanged = nextPrimaryRoleId !== currentPrimaryRoleId;
        const secondaryChanged = !arePlannerRoleIdListsEqual(normalizedNextSecondaryRoleIds, currentSecondaryRoleIds);

        if (!primaryChanged && !secondaryChanged) {
          return { status: 'skipped' as const, userId };
        }

        try {
          if (primaryChanged) {
            await updatePlannerMemberDefaultRole(userId, nextPrimaryRoleId);
          }
          if (secondaryChanged) {
            await replacePlannerMemberSecondaryRoles({
              userId,
              roleIds: normalizedNextSecondaryRoleIds,
            });
          }
          return { status: 'updated' as const, userId };
        } catch (error) {
          const plannerRoleError = new Error(
            error instanceof Error ? error.message : 'Unable to update planner roles for this member.'
          ) as Error & { userId?: string };
          plannerRoleError.userId = userId;
          throw plannerRoleError;
        }
      });

      const results = await Promise.allSettled(tasks);
      const updatedUserIds: string[] = [];
      const skippedUserIds: string[] = [];
      const failed: BulkUpdatePlannerMemberRolesResult['failed'] = [];

      results.forEach((result, index) => {
        const fallbackUserId = normalizedUserIds[index];
        if (result.status === 'fulfilled') {
          if (result.value.status === 'updated') {
            updatedUserIds.push(result.value.userId);
          } else {
            skippedUserIds.push(result.value.userId);
          }
          return;
        }

        const reason = result.reason as (Error & { userId?: string }) | undefined;
        failed.push({
          userId: reason?.userId || fallbackUserId,
          message: reason?.message || 'Unable to update planner roles for this member.',
        });
      });

      return { updatedUserIds, skippedUserIds, failed };
    },
    onSuccess: async (summary) => {
      if (summary.updatedUserIds.length > 0) {
        try {
          await autoAssignUpcomingServices();
        } catch (error) {
          console.error('Planner auto-assign failed after bulk role update:', error);
        }
      }
      await invalidatePlannerQueries();
    },
  });

  const upsertAvailabilityMutation = useMutation({
    mutationFn: upsertPlannerAvailability,
    onSuccess: async (_unused, payload) => {
      try {
        await maybeAutoAssignServicePlan(payload.service_plan_id);
      } catch (error) {
        console.error('Planner auto-assign failed after availability upsert:', error);
      }
      await invalidatePlannerQueries();
    },
  });

  const respondToServicePlanMutation = useMutation({
    mutationFn: respondToPlannerService,
    onSuccess: async (_unused, payload) => {
      try {
        await maybeAutoAssignServicePlan(payload.service_plan_id);
      } catch (error) {
        console.error('Planner auto-assign failed after response update:', error);
      }
      await invalidatePlannerQueries();
    },
  });

  const deleteAvailabilityMutation = useMutation({
    mutationFn: ({ servicePlanId, userId }: { servicePlanId: string; userId: string }) =>
      deletePlannerAvailability(servicePlanId, userId),
    onSuccess: async (_unused, vars) => {
      try {
        await maybeAutoAssignServicePlan(vars.servicePlanId);
      } catch (error) {
        console.error('Planner auto-assign failed after availability delete:', error);
      }
      await invalidatePlannerQueries();
    },
  });

  const createMemberUnavailabilityMutation = useMutation({
    mutationFn: createPlannerMemberUnavailability,
    onSuccess: async () => {
      try {
        await autoAssignUpcomingServices();
      } catch (error) {
        console.error('Planner auto-assign failed after unavailability create:', error);
      }
      await invalidatePlannerQueries();
    },
  });

  const updateMemberUnavailabilityMutation = useMutation({
    mutationFn: ({
      unavailabilityId,
      updates,
    }: {
      unavailabilityId: string;
      updates: UpdatePlannerMemberUnavailabilityInput;
    }) => updatePlannerMemberUnavailability(unavailabilityId, updates),
    onSuccess: async () => {
      try {
        await autoAssignUpcomingServices();
      } catch (error) {
        console.error('Planner auto-assign failed after unavailability update:', error);
      }
      await invalidatePlannerQueries();
    },
  });

  const deleteMemberUnavailabilityMutation = useMutation({
    mutationFn: deletePlannerMemberUnavailability,
    onSuccess: async () => {
      try {
        await autoAssignUpcomingServices();
      } catch (error) {
        console.error('Planner auto-assign failed after unavailability delete:', error);
      }
      await invalidatePlannerQueries();
    },
  });

  const addTeamRequiredRoleMutation = useMutation({
    mutationFn: addPlannerTeamRequiredRole,
    onSuccess: async () => {
      try {
        await autoAssignUpcomingServices();
      } catch (error) {
        console.error('Planner auto-assign failed after required role add:', error);
      }
      await invalidatePlannerQueries();
    },
  });

  const removeTeamRequiredRoleMutation = useMutation({
    mutationFn: removePlannerTeamRequiredRole,
    onSuccess: async () => {
      try {
        await autoAssignUpcomingServices();
      } catch (error) {
        console.error('Planner auto-assign failed after required role removal:', error);
      }
      await invalidatePlannerQueries();
    },
  });

  const setTeamManagerTeamsMutation = useMutation({
    mutationFn: setPlannerTeamManagerTeams,
    onSuccess: invalidatePlannerQueries,
  });

  const getProPresenterConnection = useCallback(async () => {
    const { data, error } = await supabase
      .from('planner_propresenter_connections')
      .select('*')
      .eq('integration_key', 'propresenter7')
      .maybeSingle();
    if (error) throw error;
    return data;
  }, []);

  const updateProPresenterConnection = useCallback(
    async (connectionId: string, updates: Record<string, unknown>) => {
      const { data, error } = await supabase
        .from('planner_propresenter_connections')
        .update(updates)
        .eq('id', connectionId)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    []
  );

  const queueProPresenterSyncNow = useCallback(async (connectionId: string) => {
    const { data, error } = await supabase.functions.invoke('planner-propresenter-sync-now', {
      body: { connectionId, direction: 'bidirectional' },
    });
    if (error || !data?.success) {
      throw error ?? new Error(data?.error || 'Unable to queue ProPresenter sync.');
    }
    return data;
  }, []);

  const getProPresenterSyncRuns = useCallback(async (limit = 20) => {
    const { data, error } = await supabase
      .from('planner_propresenter_sync_runs')
      .select('*')
      .order('queued_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return data ?? [];
  }, []);

  const getProPresenterConflicts = useCallback(async (limit = 20) => {
    const { data, error } = await supabase
      .from('planner_propresenter_conflicts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return data ?? [];
  }, []);

  const data = useMemo(() => {
    const plans = plansQuery.data ?? [];
    const plannerMembers = membersQuery.data ?? [];
    const availability = availabilityQuery.data ?? [];
    const memberUnavailability = memberUnavailabilityQuery.data ?? [];
    const requiredRoleIds = (teamRequiredRolesQuery.data ?? []).map((row) => row.role_id).filter(Boolean);
    const visiblePlans = plans.filter((plan) => !['archived', 'cancelled'].includes(String(plan.status || 'published')));
    const sortedPlans = [...visiblePlans].sort(sortPlansByDate);
    const nowMs = Date.now();
    const upcomingPlans = sortedPlans.filter((plan) => new Date(plan.service_date).getTime() >= nowMs);
    const upcomingPlansByDay = new Map<string, PlannerServicePlan[]>();
    upcomingPlans.forEach((plan) => {
      const dayKey = getLocalDateKey(plan.service_date);
      const entries = upcomingPlansByDay.get(dayKey) ?? [];
      entries.push(plan);
      upcomingPlansByDay.set(dayKey, entries);
    });
    const upcomingPlanDays: PlannerUpcomingPlanDay[] = Array.from(upcomingPlansByDay.entries()).map(
      ([dateKey, dayPlans]) => ({
        dateKey,
        serviceDate: dayPlans[0]?.service_date ?? '',
        plans: [...dayPlans].sort(sortPlansByDate),
      })
    );
    const historyPlans = sortedPlans
      .filter((plan) => new Date(plan.service_date).getTime() < nowMs)
      .sort((a, b) => new Date(b.service_date).getTime() - new Date(a.service_date).getTime());
    const nextPlan = upcomingPlans[0] ?? null;

    const myAssignments: PlannerAssignmentWithPlan[] = [];
    const peopleMap = new Map<
      string,
      {
        id: string;
        name: string;
        email: string | null;
        title: string | null;
        avatar: string | null;
        statuses: Set<string>;
        roleNames: Set<string>;
        plannerAccessRoles: Set<string>;
        defaultPlannerRoleId: string | null;
        primaryPlannerRoleId: string | null;
        secondaryPlannerRoleIds: string[];
        assignmentDates: string[];
        assignmentsCount: number;
      }
    >();
    const assignmentsByPerson = new Map<string, PlannerAssignmentWithPlan[]>();
    const availabilityByPerson = new Map<string, PlannerAvailability[]>();

    sortedPlans.forEach((plan) => {
      (plan.service_plan_assignments ?? []).forEach((assignment) => {
        if (currentUserId && assignment.user_id === currentUserId) {
          myAssignments.push({ assignment, plan });
        }

        const assignedUser = firstRelation(assignment.assigned_user);
        const role = firstRelation(assignment.service_plan_roles);
        const personId = assignment.user_id || assignedUser?.id;
        if (!personId) return;

        const existing = peopleMap.get(personId);
        const name =
          assignedUser?.full_name?.trim() ||
          assignedUser?.email?.trim() ||
          'Unknown member';
        if (!existing) {
          peopleMap.set(personId, {
            id: personId,
            name,
            email: assignedUser?.email ?? null,
            title: assignedUser?.title ?? null,
            avatar: assignedUser?.avatar ?? null,
            statuses: new Set(),
            roleNames: new Set(),
            plannerAccessRoles: new Set(),
            defaultPlannerRoleId: null,
            primaryPlannerRoleId: null,
            secondaryPlannerRoleIds: [],
            assignmentDates: [],
            assignmentsCount: 0,
          });
        }

        const person = peopleMap.get(personId);
        if (!person) return;

        person.statuses.add(normalizeStatus(assignment.status));
        if (role?.name) {
          person.roleNames.add(role.name);
        }
        person.assignmentDates.push(plan.service_date);
        person.assignmentsCount += 1;

        const personAssignments = assignmentsByPerson.get(personId) ?? [];
        personAssignments.push({ assignment, plan });
        assignmentsByPerson.set(personId, personAssignments);
      });
    });

    plannerMembers.forEach((member) => {
      const roleKeys = plannerDirectoryRoles(member.roles);
      if (!roleKeys.length) return;

      const personId = member.id;
      const existing = peopleMap.get(personId);
      const displayName =
        member.full_name?.trim() ||
        member.email?.trim() ||
        'Unknown member';

      if (!existing) {
        peopleMap.set(personId, {
          id: personId,
          name: displayName,
          email: member.email ?? null,
          title: member.title ?? null,
          avatar: member.avatar ?? null,
          statuses: new Set(),
          roleNames: new Set(),
          plannerAccessRoles: new Set(roleKeys),
          defaultPlannerRoleId: member.default_service_plan_role_id ?? null,
          primaryPlannerRoleId: member.primary_service_plan_role_id ?? member.default_service_plan_role_id ?? null,
          secondaryPlannerRoleIds: [...(member.secondary_service_plan_role_ids ?? [])],
          assignmentDates: [],
          assignmentsCount: 0,
        });
        return;
      }

      existing.name = existing.name || displayName;
      existing.email = existing.email || member.email || null;
      existing.title = existing.title || member.title || null;
      existing.avatar = existing.avatar || member.avatar || null;
      roleKeys.forEach((role) => existing.plannerAccessRoles.add(role));
      existing.defaultPlannerRoleId = member.default_service_plan_role_id ?? existing.defaultPlannerRoleId;
      existing.primaryPlannerRoleId =
        member.primary_service_plan_role_id ??
        member.default_service_plan_role_id ??
        existing.primaryPlannerRoleId;
      existing.secondaryPlannerRoleIds = [...(member.secondary_service_plan_role_ids ?? existing.secondaryPlannerRoleIds)];
    });

    availability.forEach((entry) => {
      const personAvailability = availabilityByPerson.get(entry.user_id) ?? [];
      personAvailability.push(entry);
      availabilityByPerson.set(entry.user_id, personAvailability);
    });

    const myUpcomingAssignments = [...myAssignments]
      .filter((item) => new Date(item.plan.service_date).getTime() >= nowMs)
      .sort(
        (a, b) =>
          new Date(a.plan.service_date).getTime() - new Date(b.plan.service_date).getTime()
      );

    const myHistoryAssignments = [...myAssignments]
      .filter((item) => new Date(item.plan.service_date).getTime() < nowMs)
      .sort(
        (a, b) =>
          new Date(b.plan.service_date).getTime() - new Date(a.plan.service_date).getTime()
      );

    const people: PlannerPersonSummary[] = Array.from(peopleMap.values())
      .map((person) => {
        const nextServiceDate =
          person.assignmentDates
            .filter((iso) => new Date(iso).getTime() >= nowMs)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] ?? null;

        return {
          id: person.id,
          name: person.name,
          email: person.email,
          title: person.title,
          avatar: person.avatar,
          statuses: Array.from(person.statuses),
          roleNames: Array.from(person.roleNames).sort((a, b) => a.localeCompare(b)),
          plannerAccessRoles: Array.from(person.plannerAccessRoles).sort((a, b) => a.localeCompare(b)),
          defaultPlannerRoleId: person.defaultPlannerRoleId,
          primaryPlannerRoleId: person.primaryPlannerRoleId ?? person.defaultPlannerRoleId,
          secondaryPlannerRoleIds: [...person.secondaryPlannerRoleIds],
          assignmentsCount: person.assignmentsCount,
          nextServiceDate,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    const getPersonDetails = (personId: string): PlannerPersonDetails | null => {
      const person = people.find((entry) => entry.id === personId);
      if (!person) return null;

      const personAssignments = assignmentsByPerson.get(personId) ?? [];
      const upcomingAssignments = personAssignments
        .filter((item) => new Date(item.plan.service_date).getTime() >= nowMs)
        .sort((a, b) => new Date(a.plan.service_date).getTime() - new Date(b.plan.service_date).getTime());
      const historyAssignments = personAssignments
        .filter((item) => new Date(item.plan.service_date).getTime() < nowMs)
        .sort((a, b) => new Date(b.plan.service_date).getTime() - new Date(a.plan.service_date).getTime());
      const availabilityRows = [...(availabilityByPerson.get(personId) ?? [])].sort(
        (a, b) => new Date(a.updated_at || a.created_at || 0).getTime() - new Date(b.updated_at || b.created_at || 0).getTime()
      );

      return {
        id: person.id,
        name: person.name,
        email: person.email,
        title: person.title,
        avatar: person.avatar,
        plannerAccessRoles: person.plannerAccessRoles,
        defaultPlannerRoleId: person.defaultPlannerRoleId,
        primaryPlannerRoleId: person.primaryPlannerRoleId ?? person.defaultPlannerRoleId,
        secondaryPlannerRoleIds: [...person.secondaryPlannerRoleIds],
        upcomingAssignments,
        historyAssignments,
        availability: availabilityRows,
      };
    };

    const autoAssignPreviewByPlan = new Map<string, ReturnType<typeof computePlannerAutoAssign>>();
    const autoAssignRules = (autoAssignRulesQuery.data ?? []) as PlannerAutoAssignRule[];
    const autoAssignPlans: PlannerAutoAssignPlan[] = sortedPlans.map((plan) => ({
      id: plan.id,
      service_date: plan.service_date,
      service_plan_assignments: (plan.service_plan_assignments ?? []).map((assignment) => ({
        id: assignment.id,
        service_plan_id: assignment.service_plan_id,
        role_id: assignment.role_id ?? null,
        user_id: assignment.user_id ?? null,
        status: assignment.status ?? null,
        created_at: assignment.created_at ?? null,
      })),
    }));

    if (requiredRoleIds.length) {
      autoAssignPlans.forEach((plan) => {
        const computation = computePlannerAutoAssign({
          servicePlanId: plan.id,
          plans: autoAssignPlans,
          requiredRoleIds,
          members: plannerMembers as PlannerAutoAssignMember[],
          availability: availability as PlannerAutoAssignAvailability[],
          unavailability: memberUnavailability as PlannerAutoAssignUnavailability[],
          rules: autoAssignRules,
        });
        autoAssignPreviewByPlan.set(plan.id, computation);
      });
    }

    const getAutoAssignPreview = (servicePlanId: string) =>
      autoAssignPreviewByPlan.get(servicePlanId) ?? { decisions: [], updates: [], inserts: [] };

    return {
      plans: sortedPlans,
      upcomingPlans,
      upcomingPlanDays,
      historyPlans,
      nextPlan,
      myAssignments,
      myUpcomingAssignments,
      myHistoryAssignments,
      plannerMembers,
      availability,
      memberUnavailability,
      people,
      getPersonDetails,
      getAutoAssignPreview,
    };
  }, [
    availabilityQuery.data,
    currentUserId,
    memberUnavailabilityQuery.data,
    membersQuery.data,
    plansQuery.data,
    teamRequiredRolesQuery.data,
    autoAssignRulesQuery.data,
  ]);

  const autoAssignServicePlanRoles = useCallback(async (servicePlanId: string, roleIds: string[]) => {
    const normalizedRoleIds = Array.from(new Set(roleIds.map((roleId) => roleId.trim()).filter(Boolean)));
    if (!normalizedRoleIds.length) return;

    const roleIdSet = new Set(normalizedRoleIds);
    const preview = data.getAutoAssignPreview(servicePlanId);
    const updates = preview.updates.filter((update) => roleIdSet.has(update.role_id));
    const inserts = preview.inserts.filter((insert) => roleIdSet.has(insert.role_id));
    let didMutate = false;

    try {
      for (const update of updates) {
        await updatePlannerAssignment(update.assignment_id, {
          user_id: update.user_id,
          status: update.status,
        });
        didMutate = true;
      }

      for (const insert of inserts) {
        await createPlannerAssignment({
          service_plan_id: insert.service_plan_id,
          role_id: insert.role_id,
          user_id: insert.user_id,
          status: insert.status,
        });
        didMutate = true;
      }
    } finally {
      if (didMutate) {
        await invalidatePlannerQueries();
      }
    }
  }, [data, invalidatePlannerQueries]);

  return {
    ...data,
    plansQuery,
    templatesQuery,
    membersQuery,
    availabilityQuery,
    memberUnavailabilityQuery,
    teamRequiredRolesQuery,
    teamManagersQuery,
    currentUserQuery,
    isLoading:
      plansQuery.isLoading ||
      templatesQuery.isLoading ||
      membersQuery.isLoading ||
      availabilityQuery.isLoading ||
      memberUnavailabilityQuery.isLoading ||
      teamRequiredRolesQuery.isLoading ||
      teamManagersQuery.isLoading ||
      (Boolean(currentUserId) && currentUserQuery.isLoading),
    isError:
      plansQuery.isError ||
      templatesQuery.isError ||
      membersQuery.isError ||
      availabilityQuery.isError ||
      memberUnavailabilityQuery.isError ||
      teamRequiredRolesQuery.isError ||
      teamManagersQuery.isError ||
      currentUserQuery.isError,
    error:
      plansQuery.error ??
      templatesQuery.error ??
      membersQuery.error ??
      availabilityQuery.error ??
      memberUnavailabilityQuery.error ??
      teamRequiredRolesQuery.error ??
      teamManagersQuery.error ??
      currentUserQuery.error ??
      null,
    serviceTemplates: templatesQuery.data ?? [],
    createPlan: createPlanMutation.mutateAsync,
    deletePlan: deletePlanMutation.mutateAsync,
    createServiceTemplate: createTemplateMutation.mutateAsync,
    saveServiceTemplate: saveTemplateMutation.mutateAsync,
    deleteServiceTemplate: deleteTemplateMutation.mutateAsync,
    applyServiceTemplate: applyTemplateMutation.mutateAsync,
    getPlanById,
    duplicatePlanStructure,
    createRole: createRoleMutation.mutateAsync,
    deleteRole: deleteRoleMutation.mutateAsync,
    createAssignment: createAssignmentMutation.mutateAsync,
    updateAssignment: updateAssignmentMutation.mutateAsync,
    deleteAssignment: deleteAssignmentMutation.mutateAsync,
    createSubsectionAssignment: createSubsectionAssignmentMutation.mutateAsync,
    updateSubsectionAssignment: updateSubsectionAssignmentMutation.mutateAsync,
    deleteSubsectionAssignment: deleteSubsectionAssignmentMutation.mutateAsync,
    reorderSubsection: reorderSubsectionMutation.mutateAsync,
    deleteSubsection: deleteSubsectionMutation.mutateAsync,
    renameSection: renameSectionMutation.mutateAsync,
    deleteSection: deleteSectionMutation.mutateAsync,
    updateMemberDefaultRole: updateMemberDefaultRoleMutation.mutateAsync,
    updateMemberSecondaryRoles: updateMemberSecondaryRolesMutation.mutateAsync,
    bulkUpdateMemberRoles: bulkUpdateMemberRolesMutation.mutateAsync,
    upsertAvailability: upsertAvailabilityMutation.mutateAsync,
    respondToServicePlan: respondToServicePlanMutation.mutateAsync,
    deleteAvailability: deleteAvailabilityMutation.mutateAsync,
    createMemberUnavailability: createMemberUnavailabilityMutation.mutateAsync,
    updateMemberUnavailability: updateMemberUnavailabilityMutation.mutateAsync,
    deleteMemberUnavailability: deleteMemberUnavailabilityMutation.mutateAsync,
    addTeamRequiredRole: addTeamRequiredRoleMutation.mutateAsync,
    removeTeamRequiredRole: removeTeamRequiredRoleMutation.mutateAsync,
    setTeamManagerTeams: setTeamManagerTeamsMutation.mutateAsync,
    getProPresenterConnection,
    updateProPresenterConnection,
    queueProPresenterSyncNow,
    getProPresenterSyncRuns,
    getProPresenterConflicts,
    autoAssignServicePlan,
    autoAssignServicePlanRoles,
    isMutating:
      createPlanMutation.isPending ||
      deletePlanMutation.isPending ||
      createRoleMutation.isPending ||
      deleteRoleMutation.isPending ||
      createAssignmentMutation.isPending ||
      updateAssignmentMutation.isPending ||
      deleteAssignmentMutation.isPending ||
      createSubsectionAssignmentMutation.isPending ||
      updateSubsectionAssignmentMutation.isPending ||
      deleteSubsectionAssignmentMutation.isPending ||
      reorderSubsectionMutation.isPending ||
      deleteSubsectionMutation.isPending ||
      renameSectionMutation.isPending ||
      deleteSectionMutation.isPending ||
      createTemplateMutation.isPending ||
      saveTemplateMutation.isPending ||
      deleteTemplateMutation.isPending ||
      applyTemplateMutation.isPending ||
      updateMemberDefaultRoleMutation.isPending ||
      updateMemberSecondaryRolesMutation.isPending ||
      bulkUpdateMemberRolesMutation.isPending ||
      upsertAvailabilityMutation.isPending ||
      respondToServicePlanMutation.isPending ||
      deleteAvailabilityMutation.isPending ||
      createMemberUnavailabilityMutation.isPending ||
      updateMemberUnavailabilityMutation.isPending ||
      deleteMemberUnavailabilityMutation.isPending ||
      addTeamRequiredRoleMutation.isPending ||
      removeTeamRequiredRoleMutation.isPending,
  };
}
